import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';

import type {
  CommerceMeliIntegration,
  MeliIntegrationSchema,
} from '../models/MeliIntegration';

/**
 * Los tokens de Mercado Libre no salen del navegador hacia el micro: van a
 * business, que reenvía con su propia api-key.
 */
const commerceMeliIntegrationService = createApi({
  reducerPath: 'commerceMeliIntegrationService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: ['CommerceMeliIntegration'] as const,
  endpoints: (builder) => ({
    getCommerceMeliIntegration: builder.query<
      CommerceMeliIntegration,
      { commerceId: string }
    >({
      query: ({ commerceId }) => ({
        url: `/commerces/${commerceId}/integrations/meli`,
      }),
      providesTags: ['CommerceMeliIntegration'],
    }),
    saveCommerceMeliIntegration: builder.mutation<
      CommerceMeliIntegration,
      { commerceId: string; payload: MeliIntegrationSchema }
    >({
      query: ({ commerceId, payload }) => ({
        url: `/commerces/${commerceId}/integrations/meli`,
        method: 'PUT',
        body: {
          sellerId: payload.sellerId,
          accessToken: payload.accessToken,
          ...(payload.refreshToken && { refreshToken: payload.refreshToken }),
          ...(payload.nickname && { nickname: payload.nickname }),
        },
      }),
      invalidatesTags: ['CommerceMeliIntegration'],
    }),
    unlinkCommerceMeliIntegration: builder.mutation<
      CommerceMeliIntegration,
      { commerceId: string; sellerId: string }
    >({
      query: ({ commerceId, sellerId }) => ({
        url: `/commerces/${commerceId}/integrations/meli/${sellerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CommerceMeliIntegration'],
    }),
  }),
});

export default commerceMeliIntegrationService;
export const {
  useGetCommerceMeliIntegrationQuery,
  useSaveCommerceMeliIntegrationMutation,
  useUnlinkCommerceMeliIntegrationMutation,
} = commerceMeliIntegrationService;
